import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo, breadcrumbLd, faqLd, ORIGIN } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { MapPin, Sparkles, Crown, ShirtIcon, Users, Clock, GraduationCap, BadgeCheck, Building2, ArrowRight } from "lucide-react";

function faqsFor(name, city, mascotLower, distance) {
  return [
    { q: `When is ${name} prom 2026?`, a: `The ${name} prom traditionally takes place in late April or early May; please check the official 2026 school calendar for the exact date.` },
    { q: `What are ${name} school colors?`, a: `The ${name} community rallies around their signature colors and the ${mascotLower} tradition. We stock complementary palettes on request — just ask the stylist.` },
    { q: `Where do ${name} students shop for prom dresses?`, a: `Heather's Boutique is the official formalwear partner for ${name} students, with an exclusive school suite covering prom, homecoming, and tuxedos.` },
    { q: `How far is Heather's Boutique from ${name}?`, a: `Our boutique is roughly ${distance || 28.8} miles from ${name}. Curbside pickup, on-site fitting, and delivery are all available.` },
    { q: `Where can I rent a tuxedo for ${name} prom?`, a: `Our tuxedo suite offers coordinated jackets, pants, shoes, and accessories — designed to match ${name} squad palettes for the whole prom court.` },
    { q: `What's the dress code for ${name} homecoming?`, a: `Homecoming leans semi-formal in ${city}. Our stylists help you land on the perfect midi or short cocktail dress that follows the school's guidelines.` },
    { q: `How does dress registry work for ${name}?`, a: `Every dress locked at ${name} is broadcast to every other verified student at your school in real time, so no two classmates walk in wearing the same look.` },
    { q: `Does Heather's offer group discounts for ${name} prom court?`, a: `Yes — squad packages for 4+ students unlock a stylist coordinator, matched palettes, and volume pricing across dresses, tuxes, and accessories.` },
    { q: `What's the typical price range for prom dresses at ${name}?`, a: `Most ${name} shoppers land between $328 and $698, with couture and Amarra-tier options available up to $1,200.` },
    { q: `Can I try on dresses virtually before visiting?`, a: `Yes — every ${name} student can upload a selfie and see any dress on themselves in ~15 seconds. Free first try-on per account.` },
    { q: `Does Heather's deliver to ${city}?`, a: `We deliver anywhere in Wisconsin. In-store pickup and try-on appointments are always available for ${name} families.` },
    { q: `What's the best time to start shopping for ${name} prom?`, a: `Start locking dresses 8-12 weeks before prom. Popular gowns get claimed early — the school dress registry protects your look the moment you lock it.` },
  ];
}

export default function SchoolPortal() {
  const { id } = useParams();
  const { user } = useAuth();
  const routeId = id || user?.school_id;
  const [enr, setEnr] = useState(null);

  useEffect(() => {
    if (!routeId) return;
    api.schoolEnrichment(routeId).then(({ data }) => setEnr(data)).catch(() => {});
  }, [routeId]);

  if (!routeId) return (
    <div className="h-[60vh] flex flex-col items-center justify-center gap-4">
      <p className="font-display text-3xl">Link a school first</p>
      <Link to="/account" className="bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-sm">Go to Account</Link>
    </div>
  );
  if (!enr) return <div className="h-[50vh] flex items-center justify-center"><p className="font-label text-muted-foreground">Loading…</p></div>;

  const s = enr.school;
  const mascotLower = (s.mascot || "").toLowerCase();
  const faqs = faqsFor(s.name, s.city, mascotLower, s.distance);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Schools", path: "/schools" }, { name: s.name, path: `/schools/${s.slug || s.id}` }];

  return (
    <div data-testid="school-portal" className="pb-24">
      <Seo
        title={`${s.name} Prom & Formalwear`}
        description={`Official formalwear partner for ${s.name}. Prom, homecoming & tuxedos with dress locks and AI try-on.`}
        path={`/schools/${s.slug || s.id}`}
        jsonLd={[breadcrumbLd(crumbs), faqLd(faqs), { "@context": "https://schema.org", "@type": "EducationalOrganization", name: s.name, address: { "@type": "PostalAddress", addressLocality: s.city, addressRegion: "WI", postalCode: s.zip } }]}
      />

      {/* HERO — cinematic dark gradient with school color accent */}
      <section className="relative min-h-[560px] flex items-center overflow-hidden" style={{ background: `radial-gradient(1200px 500px at 90% 10%, ${s.primary_color || '#B76E79'}55, transparent 60%), linear-gradient(180deg, #12181f 0%, #0b0f14 100%)` }}>
        <div className="absolute inset-0 opacity-30" style={{ background: `radial-gradient(600px 300px at 10% 90%, ${s.primary_color || '#B76E79'}44, transparent)` }} />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 py-20 text-ivory">
          <Breadcrumbs items={crumbs} tone="light" />
          <p className="font-label text-[10px] text-champagne tracking-widest mt-6 mb-4">HEATHER'S BOUTIQUE · OFFICIAL FORMALWEAR PARTNER</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl sm:text-7xl leading-[0.95] max-w-[820px]"
          >{s.name}</motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.8 }}
            className="font-display italic text-champagne text-2xl sm:text-3xl mt-4 max-w-xl"
          >The {s.name} Class of 2026: Defining {s.wealth_tier === "Affluent" ? "Luxury" : "Style"} at Prom 2026</motion.p>
          <p className="font-body text-sm text-ivory/70 max-w-xl mt-6 leading-relaxed">
            As the premier formalwear partner for {s.city}'s most discerning families, Heather's Boutique transforms {s.mascot || "school"} traditions into unforgettable, high-fashion red carpet moments.
          </p>
          <div className="flex items-center gap-6 font-body text-xs text-ivory/60 mt-6">
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {s.city}, WI</span>
            {s.mascot && <span className="flex items-center gap-1.5"><Crown className="w-3.5 h-3.5" /> Home of the {s.mascot}</span>}
            <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {s.distance || 28.8} mi from boutique</span>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/account" data-testid="school-unlock-cta" className="inline-flex items-center gap-2 bg-champagne text-ink px-6 py-3.5 rounded-full font-label text-[10px] tracking-widest hover:bg-ivory transition-colors">
              UNLOCK MY ACCESS <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/prom-dresses" data-testid="school-shop-cta" className="inline-flex items-center gap-2 border border-ivory/40 text-ivory px-6 py-3.5 rounded-full font-label text-[10px] tracking-widest hover:bg-ivory hover:text-ink transition-colors">
              SHOP THE COLLECTION
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        {/* ABOUT narrative */}
        <section className="mt-20">
          <p className="font-label text-[10px] text-rosemetal tracking-widest">ABOUT THE SCHOOL</p>
          <div className="mt-4 grid md:grid-cols-2 gap-8 font-body text-sm text-ink/85 leading-relaxed">
            <p>
              Nestled in the {s.wealth_tier === "Affluent" ? "lush landscapes" : "spirited community"} of {s.city}, {s.name} represents the pinnacle of {enr.school_type === "Private/Independent" ? "independent education" : "public education excellence"} in the Midwest. As home to the {s.mascot || "student body"}, this institution fosters a community of elite scholars and athletes who value heritage, excellence, and a refined lifestyle that extends far beyond the classroom.
            </p>
            <p>
              Heather's Boutique understands that for {s.name.split(" ")[0]} students, formal events are more than just school dances; they are milestones of sophisticated social life. We provide a curated, high-end experience that matches the {s.wealth_tier?.toLowerCase() || ""} standards of the {s.city} area, ensuring every student makes a singular statement.
            </p>
          </div>
        </section>

        {/* LIVE STATS strip — 4 boxes */}
        <section className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="school-live-stats">
          {[
            { icon: Users, label: `${s.name.split(" ")[0]} students viewing this week`, value: enr.viewing_this_week },
            { icon: Sparkles, label: "Dresses claimed for prom 2026", value: enr.dresses_claimed },
            { icon: ShirtIcon, label: "Tuxedos reserved", value: enr.tuxedos_reserved },
            { icon: Clock, label: "Days until Prom 2026", value: enr.days_until_prom },
          ].map((k, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="border border-border rounded-sm p-5"
              data-testid={`school-stat-${i}`}
            >
              <div className="flex items-center gap-2">
                <k.icon className="w-3.5 h-3.5 text-rosemetal" strokeWidth={1.5} />
                <span className="font-label text-[8px] text-rosemetal tracking-widest">◉ LIVE</span>
              </div>
              <div className="font-display text-4xl mt-3">{k.value}</div>
              <p className="font-body text-xs text-muted-foreground mt-1 leading-tight">{k.label}</p>
            </motion.div>
          ))}
        </section>

        {/* FACT PANEL */}
        <section className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { icon: GraduationCap, label: "ENROLLMENT", value: (s.students || "—").toLocaleString ? s.students.toLocaleString() : s.students },
            { icon: Crown, label: "TIER", value: s.wealth_tier || "—" },
            { icon: BadgeCheck, label: "NICHE GRADE", value: enr.niche_grade },
            { icon: ShirtIcon, label: "MASCOT", value: s.mascot || "—" },
          ].map((k, i) => (
            <div key={i} className="border border-border rounded-sm p-5 bg-blush/20">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <k.icon className="w-3 h-3" strokeWidth={1.5} />
                <span className="font-label text-[9px] tracking-widest">{k.label}</span>
              </div>
              <div className="font-display text-2xl mt-2">{k.value}</div>
            </div>
          ))}
        </section>

        {/* DIRECTORY - 4 category tiles (city-specific SEO deep links) */}
        <section className="mt-20">
          <p className="font-label text-[10px] text-rosemetal tracking-widest">CURATED {s.name.toUpperCase()} FORMALWEAR</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-2 leading-[0.95]">The {s.name} formalwear directory</h2>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              // City-hub links first — Bruce-Clay silo pages ranked for
              // "{City} prom dresses" long-tail queries, which are our
              // highest-intent local traffic.
              { emoji: "🎓", title: `${s.city} Prom Dresses`, desc: `Shop prom dresses in ${s.city}, WI`, to: s.city_slug ? `/prom-dresses/city/${s.city_slug}` : "/prom-dresses" },
              { emoji: "🌟", title: "Homecoming", desc: `${s.name} homecoming favorites`, to: "/homecoming-dresses" },
              { emoji: "💎", title: "All Dresses", desc: `Curated dresses for ${s.name}`, to: "/prom-dresses" },
              { emoji: "🎩", title: "Tuxedos", desc: `Tuxedo rentals for ${s.name}`, to: "/tuxedos" },
            ].map((c) => (
              <Link
                key={c.title}
                to={c.to}
                data-testid={`school-directory-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group border border-border rounded-sm p-6 hover:border-rosemetal hover:shadow-lg transition-all bg-ivory"
              >
                <div className="w-9 h-9 rounded-full bg-blush flex items-center justify-center text-xl">{c.emoji}</div>
                <div className="font-display text-xl mt-4">{c.title}</div>
                <p className="font-body text-xs text-muted-foreground mt-1">{c.desc}</p>
                <span className="font-label text-[9px] text-rosemetal mt-3 inline-flex items-center gap-1 tracking-widest">EXPLORE →</span>
              </Link>
            ))}
          </div>

          {/* Regional interlinks — reinforces the silo hierarchy for search
              engines. Only rendered when the school has geo metadata. */}
          {(s.city_slug || s.county_slug) && (
            <div
              data-testid="school-regional-links"
              className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border pt-6 font-body text-sm text-muted-foreground"
            >
              <span className="font-label text-[10px] tracking-widest text-rosemetal">EXPLORE THE AREA</span>
              {s.city_slug && (
                <Link
                  to={`/prom-dresses/city/${s.city_slug}`}
                  data-testid="school-city-link"
                  className="hover:text-ink underline underline-offset-4 decoration-rosemetal/40 hover:decoration-rosemetal transition-colors"
                >
                  All formalwear in {s.city}
                </Link>
              )}
              {s.county_slug && (
                <Link
                  to={`/schools/county/${s.county_slug}`}
                  data-testid="school-county-link"
                  className="hover:text-ink underline underline-offset-4 decoration-rosemetal/40 hover:decoration-rosemetal transition-colors"
                >
                  Schools in {s.county} County
                </Link>
              )}
              <Link
                to="/schools"
                data-testid="school-all-link"
                className="hover:text-ink underline underline-offset-4 decoration-rosemetal/40 hover:decoration-rosemetal transition-colors"
              >
                All Wisconsin schools
              </Link>
            </div>
          )}
        </section>

        {/* ABOUT + FACTS TABLE + NARRATIVE */}
        <section className="mt-24 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="font-label text-[10px] text-rosemetal tracking-widest">THE DOSSIER</p>
            <h2 className="font-display text-4xl mt-2 leading-tight">About {s.name}</h2>
            <dl className="mt-8 space-y-3">
              {[
                ["District", s.district || "—"],
                ["County", s.county || "—"],
                ["ZIP", s.zip || "—"],
                ["WIAA Division", s.wiaa_division || "—"],
                ["Grades", s.grades || "—"],
                ["Type", enr.school_type],
                ["Website", s.website ? <a href={s.website} target="_blank" rel="noopener noreferrer" className="text-rosemetal underline">Visit</a> : "—"],
                ["From Heather's", `${s.distance || 28.8} mi`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border pb-2">
                  <dt className="font-label text-[10px] text-muted-foreground tracking-widest uppercase">{k}</dt>
                  <dd className="font-body text-sm">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="font-body text-sm text-ink/85 leading-relaxed space-y-4">
            <p>{s.name} is a cornerstone of the {s.city} community, where the spirit of the {s.mascot || "students"} is matched only by a commitment to academic brilliance and athletic prowess. The campus culture is one of distinguished tradition, where school pride is worn with distinction from the ice rink to the theater stage.</p>
            <p>When homecoming season arrives, the energy in the {s.city} area shifts toward elegant semi-formal attire, while the anticipation for the {s.name} prom creates a demand for the absolute finest in luxury fashion. Every gown selected and every suit tailored must reflect the high expectations of a community used to the best.</p>
            <p>Whether it's a black-tie gala or a student-led fundraiser, the formalwear culture in this district is vibrant and fashion-forward. We specialize in bringing the latest runway trends to local students, ensuring that whether they are attending homecoming or a high-stakes athletic banquet, their presence is felt. Heather's Boutique is proud to be the destination of choice for {s.city} families seeking exclusivity and personal styling.</p>
          </div>
        </section>

        {/* UNLOCK CARD — members only invitation */}
        <section className="mt-24" data-testid="school-unlock-card">
          <div className="border border-champagne/40 rounded-sm p-8 md:p-12 bg-gradient-to-br from-blush/40 to-champagne/20">
            <p className="font-label text-[10px] text-rosemetal tracking-widest">◉ MEMBERS ONLY · INVITE INSIDE</p>
            <h2 className="font-display text-4xl mt-3 leading-tight max-w-xl">Unlock the {s.name} formalwear suite</h2>
            <p className="font-body text-sm text-muted-foreground mt-3 max-w-xl">
              Free for verified {s.name} students. Real-time prom intelligence, the live dress registry, tuxedo catalog, AI try-on, and squad coordination — all themed in your school colors.
            </p>
            <div className="mt-8 grid md:grid-cols-3 gap-3">
              {[
                { t: "Prom 2026 Intelligence", d: `Date, theme, venue, dress trends — all in one private feed for your school.` },
                { t: "Live Dress Registry", d: "See what classmates have claimed. Reserve yours. Never the same dress twice." },
                { t: "Tuxedo Suite", d: "School-themed tuxes, slim/classic fits, group coordination for prom court." },
              ].map((f) => (
                <div key={f.t} className="border border-border rounded-sm p-5 bg-ivory">
                  <div className="w-7 h-7 rounded-full bg-blush flex items-center justify-center text-sm">🎓</div>
                  <div className="font-display text-base mt-3">{f.t}</div>
                  <p className="font-body text-xs text-muted-foreground mt-1 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <Link to="/register" data-testid="school-claim-cta" className="bg-ink text-ivory px-6 py-3.5 rounded-full font-label text-[10px] tracking-widest hover:bg-rosemetal transition-colors">
                CLAIM MY {s.name.toUpperCase()} ACCESS
              </Link>
              <Link to="/login" className="font-label text-[10px] text-rosemetal tracking-widest">I ALREADY HAVE AN ACCOUNT</Link>
            </div>
            <p className="font-label text-[9px] text-muted-foreground tracking-widest mt-6">
              🔒 LOCKED TO VERIFIED STUDENTS · PARENTS CAN <Link to="/parents" className="underline">SIGN UP HERE</Link>
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-24 max-w-[900px]" data-testid="school-faq">
          <p className="font-label text-[10px] text-rosemetal tracking-widest">FREQUENTLY ASKED</p>
          <h2 className="font-display text-4xl mt-2">Everything {s.name} families ask</h2>
          <div className="mt-8 divide-y divide-border border-y border-border">
            {faqs.map((f, i) => (
              <details key={i} className="group py-5">
                <summary className="font-display text-lg cursor-pointer list-none flex justify-between items-center gap-6">
                  {f.q}
                  <span className="text-rosemetal text-2xl group-open:rotate-45 transition-transform shrink-0">+</span>
                </summary>
                <p className="font-body text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* VISIT LOCATION */}
        <section className="mt-24 border border-border rounded-sm p-8 flex flex-wrap items-center justify-between gap-4" data-testid="school-visit">
          <div>
            <p className="font-label text-[10px] text-rosemetal tracking-widest">◉ VISIT HEATHER'S</p>
            <p className="font-display text-2xl mt-2 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-rosemetal" strokeWidth={1.5} />
              W359N5002 Brown Street, Suite#211
            </p>
            <p className="font-body text-xs text-muted-foreground mt-1">Oconomowoc, WI 53066 · {s.distance || 28.8} mi from {s.name}</p>
          </div>
          <a href="https://www.google.com/maps/dir/?api=1&destination=W359N5002+Brown+Street+Oconomowoc+WI+53066" target="_blank" rel="noopener noreferrer" className="border border-ink rounded-full px-5 py-3 font-label text-[10px] tracking-widest hover:bg-ink hover:text-ivory transition-colors">GET DIRECTIONS</a>
        </section>

        {/* NEARBY SCHOOLS */}
        {enr.nearby?.length > 0 && (
          <section className="mt-24" data-testid="school-nearby">
            <p className="font-label text-[10px] text-rosemetal tracking-widest">NEIGHBORING SCHOOLS</p>
            <h2 className="font-display text-4xl mt-2">Also serving the {s.city} community</h2>
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {enr.nearby.map((n) => (
                <Link key={n.id} to={`/schools/${n.slug || n.id}`} className="group flex items-center justify-between gap-4 border border-border rounded-sm p-4 hover:border-rosemetal transition-colors">
                  <div>
                    <div className="font-display text-base group-hover:text-rosemetal transition-colors">{n.name}</div>
                    <div className="font-label text-[9px] text-muted-foreground tracking-widest mt-1">{n.city}{n.mascot ? ` · ${n.mascot}` : ""}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-rosemetal shrink-0" strokeWidth={1.5} />
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
